import { Button } from '@any-ui-react/core';
import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { ActionMaterialIcon } from '@/common/components/shared/action-material-icon/ActionMaterialIcon';
import type { ManagerialAccountingFormData, ManagerialAccountingFormDataAllocations } from '@/common/types';

interface AllocationFieldProps<K extends keyof ManagerialAccountingFormDataAllocations> {
  addLabel: string;
  errors: FieldErrors<ManagerialAccountingFormData>;
  fields: Array<{ id: string }>;
  formKey: K;
  loading: boolean;
  maxEntries: number;
  onAdd: () => void;
  onRemove: (index: number) => void;
  optional?: boolean;
  renderRowContent: (index: number) => ReactNode;
  suggestionHint?: ReactNode;
  title: string;
  total: number;
}

export const AllocationField = <K extends keyof ManagerialAccountingFormDataAllocations>({
  addLabel,
  errors,
  fields,
  formKey,
  loading,
  maxEntries,
  onAdd,
  onRemove,
  optional,
  renderRowContent,
  suggestionHint,
  title,
  total,
}: AllocationFieldProps<K>) => {
  const rounded = useMemo(() => Math.round(total * 100) / 100, [total]);
  const is100 = useMemo(() => rounded === 100, [rounded]);
  const canAdd = useMemo(() => fields.length < maxEntries, [fields, maxEntries]);

  const errorMessages = useMemo(
    () => ({
      message: errors[formKey]?.message,
      rootMessage: errors[formKey]?.root?.message,
    }),
    [formKey, errors],
  );

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 font-medium">
        <h3>{title}</h3>
        <div
          className={classNames('text-xs tabular-nums', {
            'text-green-600': is100,
            'text-failure': !optional || (!is100 && fields.length > 0),
          })}
        >
          Total: {rounded}%
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-start justify-between">
        {suggestionHint}
        <Button className="ml-auto" variant="default" disabled={loading || !canAdd} onClick={onAdd}>
          {addLabel}&nbsp;
          <span className="tabular-nums">({maxEntries - fields.length})</span>
        </Button>
      </div>
      {(errorMessages.message || errorMessages.rootMessage) && (
        <div>
          {errorMessages.message && <p className="text-2xs text-failure">{errorMessages.message}</p>}
          {errorMessages.rootMessage && <p className="text-2xs text-failure">{errorMessages.rootMessage}</p>}
        </div>
      )}
      {fields.length > 0 && (
        <div className="flex flex-col gap-y-2">
          {fields.map((field, i) => (
            <div key={field.id} className="flex items-start gap-2">
              <div className="tabular-nums text-right leading-13">{i + 1}.</div>
              {renderRowContent(i)}
              <ActionMaterialIcon iconName="delete" disabled={loading} onClick={() => onRemove(i)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
